/**
 * Garde de permission par URL (règles : route-permissions.ts — registre RBAC
 * §5, trous n°3/4/5).
 *
 * `<RequirePerm {...rule}>` enveloppe l'élément de route : si l'utilisateur
 * connecté ne satisfait pas la règle, on affiche une page « Accès non
 * autorisé » (PAS de redirection silencieuse) ; pour les pages exclues de la
 * matrice v2 (§8), une page « Page retirée » — sauf OWNER/ADMIN qui gardent
 * l'accès (portes de service pendant la transition RBAC v2).
 */

import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, ShieldAlert } from "lucide-react";
import { useAuthStore } from "@/features/auth/auth.store";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { Button, LoadingState } from "@/ui";
import { evaluateRouteAccess, type RouteRule } from "./route-permissions";

function BlockedPage({ icon, title, message }: { icon: ReactNode; title: string; message: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center text-center px-5 py-24">
      <div className="flex items-center justify-center h-11 w-11 rounded-lg border border-danger/25 bg-danger-dim text-danger mb-3">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-text mb-1">{title}</h3>
      <p className="text-xs text-text2 max-w-xs">{message}</p>
      {/* "/" = RoleAwareIndex : accueil selon le rôle (dashboard admin / tâches membre). */}
      <Button variant="ghost" size="sm" className="mt-4" onClick={() => navigate("/")}>
        Retour à l'accueil
      </Button>
    </div>
  );
}

export function RequirePerm({
  permission,
  adminOnly,
  restrictRoles,
  removed,
  children,
}: RouteRule & { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const { data: currentUser, isError } = useCurrentUser();
  const user = currentUser ?? null;

  // /auth/me pas encore résolu : ne pas flasher un refus pendant le chargement.
  if (status === "authenticated" && !currentUser && !isError) return <LoadingState />;

  const access = evaluateRouteAccess(user, { permission, adminOnly, restrictRoles, removed });
  if (access === "removed") {
    return (
      <BlockedPage
        icon={<Ban className="h-5 w-5" />}
        title="Page retirée"
        message="Cette page a été retirée de la plateforme."
      />
    );
  }
  if (access === "forbidden") {
    return (
      <BlockedPage
        icon={<ShieldAlert className="h-5 w-5" />}
        title="Accès non autorisé"
        message="Vous n'avez pas accès à cette page."
      />
    );
  }
  return <>{children}</>;
}
